'use client';

import { c as _c } from "react/compiler-runtime";
import type { ListQueryCodeApiDto } from '__generated__/api/unauth/types.gen';
import { Checkbox } from '@common/components';
import { useQuery } from '@tanstack/react-query';
import { UnauthQueryOptions } from '@/api';
import type { FilterOption } from '../../constants';
import { FilterCheckboxList } from '../FilterCheckboxList';
import { FilterChipGroup } from '../FilterChipGroup';
import { FilterDrawerShell } from '../FilterDrawerShell';
import { FilterSection } from '../FilterSection';
import { FilterSidebarShell } from '../FilterSidebarShell';
interface PolicyFilterProps {
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  onApplyDrawer: () => void;
  targetCodes: string[];
  provisionCodes: string[];
  jobCodes: string[];
  ageCodes: string[];
  includeExpired: boolean;
  onToggleIncludeExpired: () => void;
  onToggleTargetCode: (code: string) => void;
  onToggleProvisionCode: (code: string) => void;
  onToggleJobCode: (code: string) => void;
  onToggleAgeCode: (code: string) => void;
  onReset: () => void;
}
const EXPIRED_CHIP_VALUE = 'includeExpired';
const EXPIRED_CHIP_OPTIONS = [{
  value: EXPIRED_CHIP_VALUE,
  label: '마감 포함'
}];
const toFilterOptions = (codes?: Array<ListQueryCodeApiDto>): FilterOption<string>[] => (codes ?? []).filter((item): item is Required<Pick<ListQueryCodeApiDto, 'code' | 'value'>> => Boolean(item.code && item.value)).map(item => ({
  value: item.code,
  label: item.value
}));
export function PolicyFilter(t0) {
  const $ = _c(86);
  const {
    isDrawerOpen,
    onCloseDrawer,
    onApplyDrawer,
    targetCodes,
    provisionCodes,
    jobCodes,
    ageCodes,
    includeExpired,
    onToggleIncludeExpired,
    onToggleTargetCode,
    onToggleProvisionCode,
    onToggleJobCode,
    onToggleAgeCode,
    onReset
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/query/policies/codes/target"](),
      select: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: t2,
    isLoading: isTargetLoading
  } = useQuery(t1);
  let t3;
  if ($[1] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const targetOptions = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/query/policies/codes/provision"](),
      select: _temp2
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: t5,
    isLoading: isProvisionLoading
  } = useQuery(t4);
  let t6;
  if ($[4] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const provisionOptions = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/query/policies/codes/job"](),
      select: _temp3
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const {
    data: t8,
    isLoading: isJobLoading
  } = useQuery(t7);
  let t9;
  if ($[7] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const jobOptions = t9;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/query/policies/codes/age"](),
      select: _temp4
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const {
    data: t11,
    isLoading: isAgeLoading
  } = useQuery(t10);
  let t12;
  if ($[10] !== t11) {
    t12 = t11 === undefined ? [] : t11;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const ageOptions = t12;
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-Gray-800 typo-body4">마감 포함</span>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== includeExpired || $[14] !== onToggleIncludeExpired) {
    t14 = <FilterSection title={"\uC2E0\uCCAD\uAE30\uAC04"}><Checkbox checked={includeExpired} onCheckedChange={onToggleIncludeExpired}>{t13}</Checkbox></FilterSection>;
    $[13] = includeExpired;
    $[14] = onToggleIncludeExpired;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== isTargetLoading || $[17] !== onToggleTargetCode || $[18] !== targetCodes || $[19] !== targetOptions) {
    t15 = <FilterSection title={"\uB300\uC0C1"}><FilterCheckboxList options={targetOptions} selectedValues={targetCodes} onToggle={onToggleTargetCode} isLoading={isTargetLoading} skeletonCount={6} /></FilterSection>;
    $[16] = isTargetLoading;
    $[17] = onToggleTargetCode;
    $[18] = targetCodes;
    $[19] = targetOptions;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== isProvisionLoading || $[22] !== onToggleProvisionCode || $[23] !== provisionCodes || $[24] !== provisionOptions) {
    t16 = <FilterSection title={"\uC9C0\uC6D0\uD615\uD0DC"}><FilterCheckboxList options={provisionOptions} selectedValues={provisionCodes} onToggle={onToggleProvisionCode} isLoading={isProvisionLoading} skeletonCount={6} /></FilterSection>;
    $[21] = isProvisionLoading;
    $[22] = onToggleProvisionCode;
    $[23] = provisionCodes;
    $[24] = provisionOptions;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== isJobLoading || $[27] !== jobCodes || $[28] !== jobOptions || $[29] !== onToggleJobCode) {
    t17 = <FilterSection title={"\uC9C1\uC5C5"}><FilterCheckboxList options={jobOptions} selectedValues={jobCodes} onToggle={onToggleJobCode} isLoading={isJobLoading} skeletonCount={6} /></FilterSection>;
    $[26] = isJobLoading;
    $[27] = jobCodes;
    $[28] = jobOptions;
    $[29] = onToggleJobCode;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== ageCodes || $[32] !== ageOptions || $[33] !== isAgeLoading || $[34] !== onToggleAgeCode) {
    t18 = <FilterSection title={"\uC5F0\uB839"}><FilterCheckboxList options={ageOptions} selectedValues={ageCodes} onToggle={onToggleAgeCode} isLoading={isAgeLoading} skeletonCount={6} /></FilterSection>;
    $[31] = ageCodes;
    $[32] = ageOptions;
    $[33] = isAgeLoading;
    $[34] = onToggleAgeCode;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== onReset || $[37] !== t14 || $[38] !== t15 || $[39] !== t16 || $[40] !== t17 || $[41] !== t18) {
    t19 = <FilterSidebarShell onReset={onReset}>{t14}{t15}{t16}{t17}{t18}</FilterSidebarShell>;
    $[36] = onReset;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h3 className="font-700 text-Gray-900 typo-body4">신청기간</h3>;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== includeExpired) {
    t21 = includeExpired ? [EXPIRED_CHIP_VALUE] : [];
    $[44] = includeExpired;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== onToggleIncludeExpired || $[47] !== t21) {
    t22 = <div className="flex flex-col gap-12">{t20}<FilterChipGroup options={EXPIRED_CHIP_OPTIONS} selectedValues={t21} onToggle={onToggleIncludeExpired} /></div>;
    $[46] = onToggleIncludeExpired;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h3 className="font-700 text-Gray-900 typo-body4">대상</h3>;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== isTargetLoading || $[51] !== onToggleTargetCode || $[52] !== targetCodes || $[53] !== targetOptions) {
    t24 = <div className="flex flex-col gap-12">{t23}<FilterChipGroup options={targetOptions} selectedValues={targetCodes} onToggle={onToggleTargetCode} isLoading={isTargetLoading} skeletonCount={6} /></div>;
    $[50] = isTargetLoading;
    $[51] = onToggleTargetCode;
    $[52] = targetCodes;
    $[53] = targetOptions;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <h3 className="font-700 text-Gray-900 typo-body4">지원형태</h3>;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== isProvisionLoading || $[57] !== onToggleProvisionCode || $[58] !== provisionCodes || $[59] !== provisionOptions) {
    t26 = <div className="flex flex-col gap-12">{t25}<FilterChipGroup options={provisionOptions} selectedValues={provisionCodes} onToggle={onToggleProvisionCode} isLoading={isProvisionLoading} skeletonCount={6} /></div>;
    $[56] = isProvisionLoading;
    $[57] = onToggleProvisionCode;
    $[58] = provisionCodes;
    $[59] = provisionOptions;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <h3 className="font-700 text-Gray-900 typo-body4">직업</h3>;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== isJobLoading || $[63] !== jobCodes || $[64] !== jobOptions || $[65] !== onToggleJobCode) {
    t28 = <div className="flex flex-col gap-12">{t27}<FilterChipGroup options={jobOptions} selectedValues={jobCodes} onToggle={onToggleJobCode} isLoading={isJobLoading} skeletonCount={6} /></div>;
    $[62] = isJobLoading;
    $[63] = jobCodes;
    $[64] = jobOptions;
    $[65] = onToggleJobCode;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <h3 className="font-700 text-Gray-900 typo-body4">연령</h3>;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== ageCodes || $[69] !== ageOptions || $[70] !== isAgeLoading || $[71] !== onToggleAgeCode) {
    t30 = <div className="flex flex-col gap-12">{t29}<FilterChipGroup options={ageOptions} selectedValues={ageCodes} onToggle={onToggleAgeCode} isLoading={isAgeLoading} skeletonCount={6} /></div>;
    $[68] = ageCodes;
    $[69] = ageOptions;
    $[70] = isAgeLoading;
    $[71] = onToggleAgeCode;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== isDrawerOpen || $[74] !== onApplyDrawer || $[75] !== onCloseDrawer || $[76] !== onReset || $[77] !== t22 || $[78] !== t24 || $[79] !== t26 || $[80] !== t28 || $[81] !== t30) {
    t31 = <FilterDrawerShell isOpen={isDrawerOpen} onReset={onReset} onApply={onApplyDrawer} onClose={onCloseDrawer}>{t22}{t24}{t26}{t28}{t30}</FilterDrawerShell>;
    $[73] = isDrawerOpen;
    $[74] = onApplyDrawer;
    $[75] = onCloseDrawer;
    $[76] = onReset;
    $[77] = t22;
    $[78] = t24;
    $[79] = t26;
    $[80] = t28;
    $[81] = t30;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== t19 || $[84] !== t31) {
    t32 = <>{t19}{t31}</>;
    $[83] = t19;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  return t32;
}
function _temp4(data_2) {
  return toFilterOptions(data_2.context?.queryCodes);
}
function _temp3(data_1) {
  return toFilterOptions(data_1.context?.queryCodes);
}
function _temp2(data_0) {
  return toFilterOptions(data_0.context?.queryCodes);
}
function _temp(data) {
  return toFilterOptions(data.context?.queryCodes);
}